import { Link } from "@tanstack/react-router";
import {
	ArrowRight,
	CalendarRange,
	Check,
	ClipboardCheck,
	Copy,
	FileStack,
	Github,
	type LucideIcon,
	Megaphone,
	PackageCheck,
	Radio,
	Rocket,
	ShieldCheck,
	TerminalSquare,
} from "lucide-react";
import { useState } from "react";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { Reveal } from "@/components/reveal";

export type AgentWorkflowSlug =
	| "build-content-calendar"
	| "repurpose-content"
	| "launch-campaign"
	| "review-social-queue"
	| "publish-approved-posts";

type AgentWorkflow = {
	slug: AgentWorkflowSlug;
	to:
		| "/agent/build-content-calendar"
		| "/agent/repurpose-content"
		| "/agent/launch-campaign"
		| "/agent/review-social-queue"
		| "/agent/publish-approved-posts";
	title: string;
	shortTitle: string;
	description: string;
	eyebrow: string;
	icon: LucideIcon;
	accent: string;
	prompt: string;
	steps: Array<{ title: string; description: string }>;
	commands: string;
};

export const AGENT_WORKFLOWS: AgentWorkflow[] = [
	{
		slug: "build-content-calendar",
		to: "/agent/build-content-calendar",
		shortTitle: "Build the calendar",
		title: "Build a Content Calendar with an AI Agent",
		eyebrow: "Plan the system",
		description:
			"Turn campaign goals, channels, assets, and review capacity into a deliberate publishing rhythm with platform-native drafts.",
		icon: CalendarRange,
		accent: "text-blue-600 dark:text-blue-400",
		prompt:
			"Use the AgentReacher social publishing skill. Build a four-week content calendar from my goals, audience, source material, connected channels, and review capacity. Define durable content pillars, choose a realistic cadence, create platform-native drafts in AgentReacher, and show me the full review queue. Do not schedule or publish until I approve the dates and final copy.",
		steps: [
			{
				title: "Inspect",
				description:
					"Read the active workspace, connected accounts, limits, and timezone.",
			},
			{
				title: "Structure",
				description:
					"Define the campaign arc, content pillars, channel roles, and cadence.",
			},
			{
				title: "Draft",
				description:
					"Create distinct platform variants as AgentReacher drafts.",
			},
			{
				title: "Review",
				description:
					"Return the calendar, missing assets, assumptions, and approval points.",
			},
		],
		commands: [
			"# Inspect the publishing surface",
			"agentreacher run workspaceContext --pretty",
			"agentreacher run integrationList --pretty",
			"",
			"# Create each approved calendar row as a draft",
			"agentreacher run postCompose --input @draft.json --pretty",
			"",
			"# Review the resulting queue",
			'agentreacher run postPlan --input \'{"goal":"list","filters":{"limit":50}}\' --pretty',
		].join("\n"),
	},
	{
		slug: "repurpose-content",
		to: "/agent/repurpose-content",
		shortTitle: "Repurpose a source",
		title: "Repurpose One Source into a Social Campaign",
		eyebrow: "Extract, then adapt",
		description:
			"Turn a changelog, blog, transcript, demo, or launch note into a compact campaign without cloning the same caption everywhere.",
		icon: FileStack,
		accent: "text-emerald-600 dark:text-emerald-400",
		prompt:
			"Use the AgentReacher social publishing skill. Repurpose this source into a one-week campaign. First extract the core promise, strongest proof, atomic ideas, objections, and visual opportunities. Then create native variants for my connected platforms, validate each target's requirements, and save the results as drafts. Preserve the source facts and wait for my approval before scheduling.",
		steps: [
			{
				title: "Distill",
				description:
					"Extract reusable ideas, proof, story moments, and claims that need verification.",
			},
			{
				title: "Select",
				description:
					"Choose only the ideas and formats that genuinely fit each channel.",
			},
			{
				title: "Adapt",
				description: "Change the hook, depth, CTA, and media plan by platform.",
			},
			{
				title: "Package",
				description:
					"Create reviewable drafts with source fidelity and approval notes.",
			},
		],
		commands: [
			"# Check requirements for the selected targets",
			"agentreacher run postSchema --input @schema-check.json --pretty",
			"",
			"# Import source media when needed",
			"agentreacher run mediaPrepare --input @media.json --pretty",
			"",
			"# Create a platform-native draft",
			"agentreacher run postCompose --input @draft.json --pretty",
		].join("\n"),
	},
	{
		slug: "launch-campaign",
		to: "/agent/launch-campaign",
		shortTitle: "Run a launch",
		title: "Run a Product Launch from Your AI Agent",
		eyebrow: "Sequence the story",
		description:
			"Build an announcement, explanation, proof, objection, and reminder sequence around a launch date, with risky actions gated.",
		icon: Rocket,
		accent: "text-orange-600 dark:text-orange-400",
		prompt:
			"Use the AgentReacher social publishing skill to build a launch campaign from this brief. Create a sequence for announcement, how it works, proof, objection handling, and reminder posts. Adapt every post to its platform, identify missing media or claims, and create drafts in AgentReacher. Show me the exact launch queue and wait for approval before scheduling it.",
		steps: [
			{
				title: "Frame",
				description:
					"Identify the launch promise, audience tension, proof, and claims boundary.",
			},
			{
				title: "Sequence",
				description:
					"Build narrative momentum before, during, and after launch day.",
			},
			{
				title: "Produce",
				description:
					"Create the copy, media plan, platform overrides, and fallbacks.",
			},
			{
				title: "Gate",
				description:
					"Hold the final schedule until dates, copy, and assets are approved.",
			},
		],
		commands: [
			"# Draft the launch queue",
			"agentreacher run postCompose --input @launch-draft.json --pretty",
			"",
			"# Inspect one post before approval",
			'agentreacher run postPlan --input \'{"goal":"inspect","postId":"POST_ID"}\' --pretty',
			"",
			"# After approval, create the scheduled version",
			"agentreacher run postCompose --input @approved-schedule.json --pretty",
		].join("\n"),
	},
	{
		slug: "review-social-queue",
		to: "/agent/review-social-queue",
		shortTitle: "Review the queue",
		title: "Review and Repair Your Social Queue",
		eyebrow: "Quality control",
		description:
			"Inspect next week's posts for repeated angles, missing media, invalid settings, weak hooks, and cadence problems before they become live mistakes.",
		icon: ClipboardCheck,
		accent: "text-violet-600 dark:text-violet-400",
		prompt:
			"Use the AgentReacher social publishing skill to review my queue for the next seven days. Inspect every post and delivery. Flag repeated angles, near-duplicate copy, missing media, invalid platform settings, weak hooks, timing collisions, and unverified claims. Propose the smallest useful edits and show a before/after summary. Do not revise, cancel, delete, schedule, or publish anything until I approve the exact changes.",
		steps: [
			{
				title: "List",
				description:
					"Load the upcoming queue and inspect every target delivery.",
			},
			{
				title: "Compare",
				description:
					"Find repetition, collisions, campaign gaps, and platform mismatch.",
			},
			{
				title: "Validate",
				description:
					"Check media, provider settings, timing, and factual claims.",
			},
			{
				title: "Patch",
				description:
					"Propose focused revisions and preserve already-approved copy.",
			},
		],
		commands: [
			"# Load the current queue",
			'agentreacher run postPlan --input \'{"goal":"list","filters":{"limit":50}}\' --pretty',
			"",
			"# Inspect a specific post and its deliveries",
			'agentreacher run postStatus --input \'{"postId":"POST_ID"}\' --pretty',
			"",
			"# Apply only the approved changes",
			"agentreacher run postPlan --input @approved-revision.json --pretty",
		].join("\n"),
	},
	{
		slug: "publish-approved-posts",
		to: "/agent/publish-approved-posts",
		shortTitle: "Publish approved work",
		title: "Publish Approved Posts and Track Every Delivery",
		eyebrow: "Operate safely",
		description:
			"Move approved work through media preparation, publishing, asynchronous processing, and per-channel delivery checks without hiding partial failures.",
		icon: Radio,
		accent: "text-rose-600 dark:text-rose-400",
		prompt:
			"Use the AgentReacher social publishing skill. Review the exact approved posts and target accounts with me first. After I explicitly approve the final payload, publish those posts, monitor every delivery until it settles, and report published, processing, and failed targets separately. Do not retry, disconnect, delete, or change copy without another approval.",
		steps: [
			{
				title: "Confirm",
				description:
					"Show exact copy, media, timing, and target account before execution.",
			},
			{
				title: "Publish",
				description:
					"Send only the posts covered by the user's explicit approval.",
			},
			{
				title: "Observe",
				description:
					"Poll asynchronous Instagram and video deliveries until they settle.",
			},
			{
				title: "Report",
				description:
					"Separate successes, processing targets, and actionable failures.",
			},
		],
		commands: [
			"# Final read-only check",
			'agentreacher run postStatus --input \'{"postId":"POST_ID"}\' --pretty',
			"",
			"# Run only after explicit approval",
			'agentreacher run postPublish --input \'{"mode":"now","postId":"POST_ID"}\' --pretty',
			"",
			"# Verify every provider delivery",
			'agentreacher run postStatus --input \'{"postId":"POST_ID"}\' --pretty',
		].join("\n"),
	},
];

const GITHUB_URL = "https://github.com/agentreacher/agentreacher-cli";
const NPM_URL = "https://www.npmjs.com/package/agentreacher";

function CopyButton({
	value,
	label = "Copy",
}: {
	value: string;
	label?: string;
}) {
	const [copied, setCopied] = useState(false);
	async function copy() {
		await navigator.clipboard.writeText(value);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1600);
	}
	return (
		<button
			type="button"
			onClick={copy}
			className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium transition-colors hover:bg-accent"
		>
			{copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
			{copied ? "Copied" : label}
		</button>
	);
}

function TerminalPanel({ code, title }: { code: string; title: string }) {
	return (
		<div className="overflow-hidden rounded-xl border border-stone-800 bg-stone-950 text-stone-100 shadow-2xl shadow-black/15">
			<div className="flex items-center justify-between border-b border-stone-800 px-4 py-3">
				<div className="flex items-center gap-2">
					<TerminalSquare className="size-4 text-orange-400" />
					<span className="font-mono text-xs text-stone-400">{title}</span>
				</div>
				<CopyButton value={code} label="Copy commands" />
			</div>
			<pre className="overflow-x-auto p-5 font-mono text-xs leading-6 sm:p-6 sm:text-[13px]">
				<code>{code}</code>
			</pre>
		</div>
	);
}

export function AgentCliOverview() {
	return (
		<MarketingPageShell>
			<section className="relative overflow-hidden rounded-md bg-white px-6 py-14 dark:bg-black sm:px-10 sm:py-20">
				<div className="pointer-events-none absolute inset-0 opacity-60 [background-image:linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] [background-size:40px_40px] [mask-image:linear-gradient(to_bottom,black,transparent_88%)]" />
				<div className="relative mx-auto max-w-4xl text-center">
					<div className="mx-auto flex w-fit items-center gap-2 rounded-full border border-border bg-background/90 px-3 py-1.5 font-mono text-xs text-muted-foreground shadow-sm">
						<span className="size-1.5 rounded-full bg-emerald-500" />
						CLI + installable agent skill
					</div>
					<h1 className="mx-auto mt-6 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-6xl">
						Give your AI agent a{" "}
						<span className="text-orange-600 dark:text-orange-400">
							publishing desk.
						</span>
					</h1>
					<p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
						The AgentReacher CLI and skill turn briefs into platform-native
						drafts, reviewable schedules, and monitored deliveries, while
						keeping the final publish action behind your approval.
					</p>
					<div className="mt-8 flex flex-wrap items-center justify-center gap-3">
						<Link
							to="/signup"
							className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
						>
							Start with AgentReacher <ArrowRight className="size-4" />
						</Link>
						<a
							href={GITHUB_URL}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium hover:bg-accent"
						>
							<Github className="size-4" /> View source
						</a>
					</div>
				</div>
			</section>

			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-16">
				<div className="grid items-center gap-10 lg:grid-cols-[0.8fr_1.2fr]">
					<Reveal>
						<p className="font-mono text-xs uppercase tracking-[0.18em] text-orange-600 dark:text-orange-400">
							Three commands
						</p>
						<h2 className="mt-3 text-3xl font-semibold tracking-[-0.04em]">
							Install once. Operate from any agent.
						</h2>
						<p className="mt-4 max-w-lg text-sm leading-7 text-muted-foreground sm:text-base">
							The skill supplies the editorial workflow. The CLI supplies
							deterministic actions. MCP connects the same tools directly to
							Claude, Cursor, Codex, Windsurf, and other compatible clients.
						</p>
						<div className="mt-6 flex flex-wrap gap-3">
							<a
								href={NPM_URL}
								target="_blank"
								rel="noreferrer"
								className="text-sm font-medium underline underline-offset-4"
							>
								npm package
							</a>
							<a
								href={
									GITHUB_URL +
									"/tree/main/skills/agentreacher-social-publishing"
								}
								target="_blank"
								rel="noreferrer"
								className="text-sm font-medium underline underline-offset-4"
							>
								skill source
							</a>
						</div>
					</Reveal>
					<TerminalPanel
						title="agentreacher-cli"
						code={[
							"# Install the publishing skill",
							"npx skills add agentreacher/agentreacher-cli",
							"",
							"# Install the CLI",
							"npm install -g agentreacher",
							"",
							"# Connect your agent and workspace",
							"agentreacher init",
						].join("\n")}
					/>
				</div>
			</section>

			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-16">
				<div className="max-w-2xl">
					<p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
						Five useful outcomes
					</p>
					<h2 className="mt-3 text-3xl font-semibold tracking-[-0.04em]">
						Ask for the outcome, not the command.
					</h2>
				</div>
				<div className="mt-10 grid gap-px overflow-hidden rounded-xl border border-border bg-border md:grid-cols-2">
					{AGENT_WORKFLOWS.map((workflow, index) => {
						const Icon = workflow.icon;
						return (
							<Reveal key={workflow.slug} delay={index * 50} className="h-full">
								<Link
									to={workflow.to}
									className="group flex h-full min-h-56 flex-col bg-background p-6 transition-colors hover:bg-muted/40 sm:p-8"
								>
									<div className="flex items-center justify-between">
										<span className="flex size-10 items-center justify-center rounded-lg border border-border bg-background">
											<Icon className={`size-5 ${workflow.accent}`} />
										</span>
										<span className="font-mono text-xs text-muted-foreground">
											0{index + 1}
										</span>
									</div>
									<h3 className="mt-8 text-xl font-semibold tracking-tight">
										{workflow.shortTitle}
									</h3>
									<p className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">
										{workflow.description}
									</p>
									<span className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
										Open workflow{" "}
										<ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
									</span>
								</Link>
							</Reveal>
						);
					})}
				</div>
			</section>

			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-16">
				<div className="mx-auto max-w-4xl">
					<div className="text-center">
						<p className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400">
							The operating loop
						</p>
						<h2 className="mt-3 text-3xl font-semibold tracking-[-0.04em]">
							Brief → draft → approve → deliver.
						</h2>
					</div>
					<div className="mt-10 grid gap-4 md:grid-cols-4">
						{[
							[
								PackageCheck,
								"01",
								"Brief",
								"Facts, audience, proof, channels, constraints.",
							],
							[
								Megaphone,
								"02",
								"Draft",
								"Native copy, media, timing, provider config.",
							],
							[
								ShieldCheck,
								"03",
								"Approve",
								"Exact queue shown before consequential actions.",
							],
							[
								Radio,
								"04",
								"Deliver",
								"Every target monitored through success or failure.",
							],
						].map(([Icon, number, title, text]) => {
							const StepIcon = Icon as LucideIcon;
							return (
								<div
									key={String(title)}
									className="rounded-xl border border-border p-5"
								>
									<div className="flex items-center justify-between">
										<StepIcon className="size-5 text-orange-500" />
										<span className="font-mono text-xs text-muted-foreground">
											{String(number)}
										</span>
									</div>
									<h3 className="mt-8 font-semibold">{String(title)}</h3>
									<p className="mt-2 text-sm leading-6 text-muted-foreground">
										{String(text)}
									</p>
								</div>
							);
						})}
					</div>
				</div>
			</section>
		</MarketingPageShell>
	);
}

export function AgentWorkflowPage({ slug }: { slug: AgentWorkflowSlug }) {
	const workflow = AGENT_WORKFLOWS.find((item) => item.slug === slug);
	if (!workflow) return null;
	const Icon = workflow.icon;
	return (
		<MarketingPageShell>
			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-16">
				<div className="mx-auto max-w-4xl">
					<Link
						to="/agent"
						className="text-sm text-muted-foreground transition-colors hover:text-foreground"
					>
						← AI agent workflows
					</Link>
					<div className="mt-8 grid items-end gap-8 lg:grid-cols-[1fr_auto]">
						<div>
							<div className="flex items-center gap-3">
								<span className="flex size-10 items-center justify-center rounded-lg border border-border">
									<Icon className={`size-5 ${workflow.accent}`} />
								</span>
								<p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
									{workflow.eyebrow}
								</p>
							</div>
							<h1 className="mt-6 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">
								{workflow.title}
							</h1>
							<p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground">
								{workflow.description}
							</p>
						</div>
						<CopyButton value={workflow.prompt} label="Copy the workflow" />
					</div>
				</div>
			</section>

			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-16">
				<div className="mx-auto max-w-4xl">
					<div className="flex flex-wrap items-end justify-between gap-4">
						<div>
							<p className="font-mono text-xs uppercase tracking-[0.18em] text-orange-600 dark:text-orange-400">
								Copyable prompt
							</p>
							<h2 className="mt-2 text-2xl font-semibold tracking-tight">
								Give this to your agent.
							</h2>
						</div>
						<CopyButton value={workflow.prompt} label="Copy prompt" />
					</div>
					<blockquote className="mt-6 rounded-xl border border-border bg-muted/30 p-6 text-base leading-8 sm:p-8">
						{workflow.prompt}
					</blockquote>
				</div>
			</section>

			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-16">
				<div className="mx-auto max-w-4xl">
					<p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
						What the agent does
					</p>
					<div className="mt-8 divide-y divide-border border-y border-border">
						{workflow.steps.map((step, index) => (
							<div
								key={step.title}
								className="grid gap-4 py-6 sm:grid-cols-[4rem_12rem_1fr] sm:items-start"
							>
								<span className="font-mono text-xs text-muted-foreground">
									0{index + 1}
								</span>
								<h3 className="font-semibold">{step.title}</h3>
								<p className="text-sm leading-6 text-muted-foreground">
									{step.description}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-16">
				<div className="mx-auto max-w-4xl">
					<TerminalPanel title={workflow.slug} code={workflow.commands} />
				</div>
			</section>

			<section className="rounded-md bg-white px-6 py-12 dark:bg-black sm:px-10 sm:py-16">
				<div className="mx-auto max-w-4xl">
					<h2 className="text-2xl font-semibold tracking-tight">
						More agent workflows
					</h2>
					<div className="mt-6 grid gap-3 sm:grid-cols-2">
						{AGENT_WORKFLOWS.filter((item) => item.slug !== slug)
							.slice(0, 4)
							.map((item) => (
								<Link
									key={item.slug}
									to={item.to}
									className="group flex items-center justify-between rounded-lg border border-border p-4 text-sm font-medium transition-colors hover:bg-accent"
								>
									{item.shortTitle}
									<ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
								</Link>
							))}
					</div>
				</div>
			</section>
		</MarketingPageShell>
	);
}
